import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CancelError, CancellablePromise } from '@atlassian/search-client/cancellable-promise';

import { onDropdownResultsShown } from '../../../common/utils/events/dropdown-results-shown';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import { type onError } from '../../error-boundary/log-exception';
import { useLogException } from '../../error-boundary/utils';
import { useBootstrap } from '../../store/bootstrap';
import {
	useDropdownActions,
	useDropdownLoading,
	usePrefetchedRecentSearchResults,
	usePrefetchedRecentViewsResults,
} from '../../store/dropdown';
import { useInputQuery } from '../../store/search';
import { conditionalRendering } from '../conditional-rendering';
import {
	type FetchAutoSuggestionParams,
	fetchAutoSuggestionResults,
} from '../fetch-auto-suggestion-results';
import {
	type FetchPeopleAndTeamsParams,
	fetchPeopleAndTeamsResults,
} from '../fetch-people-and-teams-results';
import { filterRecentSearchResults } from '../fetch-recent-search-results';
import { filterRecentViewsResults } from '../fetch-recent-views-results';

type FetchAllReturnType = Awaited<ReturnType<typeof fetchAll>>;

const fetchAll = async (
	query: string,
	peopleParams: FetchPeopleAndTeamsParams,
	autoSuggestionParams: FetchAutoSuggestionParams,
	onError: onError,
) => {
	return Promise.all([
		fetchPeopleAndTeamsResults(query, peopleParams, onError),
		fetchAutoSuggestionResults(query, autoSuggestionParams, onError),
	]);
};

export const useFetchAll = (): void => {
	const [inputQuery] = useInputQuery();
	const [{ cloudId, user, primaryProduct, orgId, analyticsAttributes }] = useBootstrap();

	const peopleParams: FetchPeopleAndTeamsParams = useMemo(
		() => ({
			primaryProduct,
			cloudId,
			userId: user?.id ?? undefined,
			orgId,
		}),
		[primaryProduct, cloudId, user?.id, orgId],
	);

	const autoSuggestionParams: FetchAutoSuggestionParams = useMemo(
		() => ({
			primaryProduct,
			cloudId,
			activationId: analyticsAttributes?.activationId,
		}),
		[primaryProduct, cloudId, analyticsAttributes?.activationId],
	);

	const [dropdownLoading] = useDropdownLoading();
	const requestRef = useRef<CancellablePromise<FetchAllReturnType>>();
	const [currentInputQuery, setCurrentInputQuery] = useState<string>(inputQuery);
	const [recentSearchResults] = usePrefetchedRecentSearchResults();
	const [recentViewsResults] = usePrefetchedRecentViewsResults();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const logException = useLogException();

	const {
		setPeopleAndTeamsResults,
		setRecentViewsResults,
		setAutoSuggestionResults,
		setRecentSearchResults,
		setDropdownLoading,
		setResultCount,
	} = useDropdownActions();

	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchData = useCallback(
		async (query: string) => {
			setCurrentInputQuery(inputQuery);
			if (requestRef.current) {
				// If there is an existing fetchAll promise, cancel it
				requestRef.current.cancel();
			}
			setDropdownLoading(true);

			// Set the fetchAll promise
			requestRef.current = new CancellablePromise(
				fetchAll(query, peopleParams, autoSuggestionParams, logException),
			);
			try {
				const [peopleAndTeamsResults, autoSuggestionResults] = await requestRef.current.promise();

				// update store
				setPeopleAndTeamsResults(peopleAndTeamsResults);
				setAutoSuggestionResults(autoSuggestionResults);
				const filteredRecentSearchResults = filterRecentSearchResults(recentSearchResults, query);
				setRecentSearchResults(filteredRecentSearchResults);
				const filteredRecentViewsResults = filterRecentViewsResults(
					recentViewsResults ?? [],
					query,
				);
				setRecentViewsResults(filteredRecentViewsResults);

				const resultCounts = conditionalRendering(query, {
					peopleAndTeams: peopleAndTeamsResults.results.length,
					autoSuggestion: autoSuggestionResults.results.length,
					recentSearch: filteredRecentSearchResults.length,
					recentViews: filteredRecentViewsResults.length,
				});
				setResultCount(resultCounts);

				const queryDurations = {
					peopleAndTeams: peopleAndTeamsResults.duration,
					autoSuggestion: autoSuggestionResults.duration,
				};

				fireAnalyticsEvent(
					// TODO: Move this to where the results are actually rendered
					onDropdownResultsShown({
						commonAttributes,
						nonPrivacySafeAttributes,
						count: resultCounts,
						duration: queryDurations,
					}),
				);

				// stop loading
				setDropdownLoading(false);
			} catch (error) {
				if (!(error instanceof CancelError)) {
					// stop loading
					setDropdownLoading(false);
					// log error
					logException(error);
				}
			}
		},
		[
			autoSuggestionParams,
			commonAttributes,
			fireAnalyticsEvent,
			inputQuery,
			logException,
			nonPrivacySafeAttributes,
			peopleParams,
			recentSearchResults,
			recentViewsResults,
			setAutoSuggestionResults,
			setDropdownLoading,
			setPeopleAndTeamsResults,
			setRecentSearchResults,
			setRecentViewsResults,
			setResultCount,
		],
	);

	useEffect(() => {
		if (
			(inputQuery !== currentInputQuery || (dropdownLoading && !requestRef.current)) &&
			recentViewsResults !== undefined
		) {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			debounceTimeout.current = setTimeout(() => {
				fetchData(inputQuery);
			}, 300);
		}
	}, [inputQuery, currentInputQuery, dropdownLoading, recentViewsResults, fetchData]);
};
